// P2b-3 round-1 polish Fix 3: shared template-rendering context for
// per-row content cells on the Meta + Google grids.
//
// The grids snapshot ad copy, headlines, descriptions, paths etc. as
// raw strings authored by the AM. Many of those strings carry
// placeholder tokens like {locationName}, {suburb}, {gymInfo},
// {campaignDescriptor}, {monthYear}. The role-grid cells used to
// render those raw strings without ever calling renderTemplate, so
// buyers saw "{gymInfo}" literally in the cell. This helper centralises
// the per-row context build + render so each cell call site is a one-
// liner and the context stays in sync between Meta and Google.
//
// Pure: same I/O surface as src/lib/templating.ts's renderTemplate,
// just with the row-derived bits pre-bundled.

import type { Account } from '../types/account.js';
import type { AccountCampaign } from '../types/accountCampaign.js';
import type { Campaign, GoogleHeadline } from '../types/campaign.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';
import type { Rollout } from '../types/rollout.js';
import { resolveMonthYear } from './monthYear.js';
import { renderTemplate } from './templating.js';

export type RowRenderContext = {
  account: Pick<Account, 'name' | 'slug'>;
  locationProfile: FranchiseLocationProfile | null;
  brandProfile: FranchiseProfile | null;
  // Pre-resolved extras passed through to renderTemplate.
  campaignDescriptor: string | null;
  monthYear: string | null;
};

// Build the context once per row. Same descriptor precedence as
// resolveRowTemplates.ts (Fix 1): a non-empty row snapshot wins,
// otherwise the campaign's descriptor. monthYear comes from the
// rollout-level label.
export function buildRowRenderContext(args: {
  row: Pick<AccountCampaign, 'campaignDescriptor'>;
  campaign: Campaign | null;
  account: Pick<Account, 'name' | 'slug'>;
  locationProfile: FranchiseLocationProfile | null;
  brandProfile: FranchiseProfile | null;
  rollout: Pick<Rollout, 'monthYearLabel'>;
}): RowRenderContext {
  const { row, campaign, account, locationProfile, brandProfile, rollout } = args;
  const rowDescriptor = row.campaignDescriptor;
  const descriptor =
    typeof rowDescriptor === 'string' && rowDescriptor.length > 0
      ? rowDescriptor
      : campaign?.campaignDescriptor ?? null;
  const monthYear = resolveMonthYear(rollout.monthYearLabel);
  return {
    account,
    locationProfile,
    brandProfile,
    campaignDescriptor: descriptor,
    monthYear,
  };
}

// Render a single template string with the row context. Returns the
// raw string back unchanged when there are no placeholders to swap;
// missing-placeholder tokens stay literal so authors notice typos.
export function renderRowTemplate(value: string | null | undefined, ctx: RowRenderContext): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const { output } = renderTemplate(value, ctx.account, ctx.locationProfile, ctx.brandProfile, {
    campaignDescriptor: ctx.campaignDescriptor,
    monthYear: ctx.monthYear,
  });
  return output;
}

// Convenience for array-typed content fields (headlines, descriptions,
// body copy variants). Drops null-ish entries; preserves order;
// returns a new array.
export function renderRowTemplateArray(
  values: string[] | null | undefined,
  ctx: RowRenderContext,
): string[] {
  if (!values) return [];
  const out: string[] = [];
  for (const v of values) {
    if (typeof v !== 'string' || v.length === 0) continue;
    out.push(renderRowTemplate(v, ctx));
  }
  return out;
}

// Pinned Headlines spec §1: Google headlines carry pin metadata.
// Templates the `text` field per headline and preserves the
// `pinPosition` verbatim. Drops headlines with empty / non-string text
// — same skip rule as renderRowTemplateArray. Returns a fresh array of
// objects so the caller can pass texts/pins into read-only displays
// without further denormalisation.
export function renderRowHeadlineArray(
  values: GoogleHeadline[] | null | undefined,
  ctx: RowRenderContext,
): GoogleHeadline[] {
  if (!values) return [];
  const out: GoogleHeadline[] = [];
  for (const h of values) {
    if (!h || typeof h.text !== 'string' || h.text.length === 0) continue;
    out.push({ text: renderRowTemplate(h.text, ctx), pinPosition: h.pinPosition ?? null });
  }
  return out;
}

// Google Ads path-slot slugifier. Path2 commonly resolves through
// {shortSuburb} / {suburb} which produce real-world location names
// like "Botanic Ridge" — Google Ads paths reject spaces. Apply this
// AFTER renderRowTemplate so the substituted value is the one we
// reshape, not the raw template.
//
// Rules (case preserved deliberately — Google Ads accepts mixed case
// and AMs use it for visual clarity):
//   - whitespace runs → single hyphen
//   - drop everything that isn't a letter / digit / hyphen
//   - collapse hyphen runs → single hyphen
//   - trim leading + trailing hyphens
//
// "Botanic Ridge"           → "Botanic-Ridge"
// "Sydney CBD!"             → "Sydney-CBD"
// "  Multiple   Spaces  "   → "Multiple-Spaces"
// "---"                     → ""
// ""                        → ""
//
// Path1 is intentionally NOT slugified — call sites for path1 stay
// pass-through. Only path2 gets this final post-processing pass.
export function slugifyPath(value: string): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  return value
    // Replace any whitespace run with a single hyphen.
    .replace(/\s+/g, '-')
    // Drop anything that isn't ASCII letter / digit / hyphen.
    .replace(/[^A-Za-z0-9-]/g, '')
    // Collapse consecutive hyphens (created by stripping in the line
    // above OR by raw input like "Sydney--CBD").
    .replace(/-+/g, '-')
    // Trim leading + trailing hyphens.
    .replace(/^-+|-+$/g, '');
}
